// src/pages/account/AddressesPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, Pencil, Trash2, Star, AlertCircle } from "lucide-react";
import Swal from "../../utils/apparifySwal";

import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { useAddress } from "../../hooks/useAddress";
import { isAddressDefault } from "../../utils/address";

import AddressModal from "../../components/checkout/AddressModal";
import { formatAddress } from "../../components/checkout/AddressList";
import { ListSkeleton } from "../../components/skeletons/ShopperSkeletons";

const PANEL_CLASS_NAME = "rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900";
const PRIMARY_TEXT_CLASS_NAME = "text-neutral-900 dark:text-neutral-50";
const SECONDARY_TEXT_CLASS_NAME = "text-neutral-500 dark:text-neutral-400";
const LABEL_BADGE_CLASS_NAME = "text-[10px] font-medium px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-500 uppercase dark:border-neutral-700 dark:text-neutral-400";
const EDIT_BUTTON_CLASS_NAME = "p-1.5 rounded-lg transition text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-200 dark:hover:bg-neutral-800";
const DELETE_BUTTON_CLASS_NAME = "p-1.5 rounded-lg transition text-neutral-400 hover:text-rose-500 hover:bg-rose-50 dark:text-neutral-500 dark:hover:text-rose-400 dark:hover:bg-rose-900/20";
const LINK_BUTTON_CLASS_NAME = "flex items-center gap-1 text-[11px] font-medium text-indigo-600 transition hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300";
const MODAL_CANCEL_BUTTON_CLASS_NAME = "flex-1 py-2.5 rounded-xl text-sm font-semibold border border-neutral-200 text-neutral-600 transition hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800";

/**
 * AddressesPage – Profile page for managing user addresses.
 * 
 * Features:
 * - View all saved addresses
 * - Add new address
 * - Edit existing address
 * - Delete address
 * - Set default address
 * - Empty state handling
 * - Error handling
 */
export default function AddressesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { logout } = useAuth();
  
  const {
    addresses,
    loading,
    error,
    selectedId,
    setSelectedId,
    load,
    createAddr,
    updateAddr,
    deleteAddr,
    setDefault,
  } = useAddress();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    initialView: "list",
    initialEditingAddress: null,
  });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [initialLoadPending, setInitialLoadPending] = useState(true);

  // Load addresses on mount
  useEffect(() => {
    let active = true;
    setInitialLoadPending(true);
    load()
      .catch((err) => {
        if (err?.status === 401) {
          logout();
          navigate("/login", { replace: true });
        }
      })
      .finally(() => {
        if (active) setInitialLoadPending(false);
      });

    return () => {
      active = false;
    };
  }, [load, logout, navigate]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalConfig({ initialView: "list", initialEditingAddress: null });
  }, []);

  const handleAddNew = useCallback(() => {
    setModalConfig({ initialView: "form", initialEditingAddress: null });
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((addr) => {
    setModalConfig({ initialView: "form", initialEditingAddress: addr });
    setModalOpen(true);
  }, []);

  const handleConfirmSelection = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  const handleCreate = useCallback(
    async (data) => {
      try {
        const created = await createAddr(data, { selectCreated: false });
        handleCloseModal();
        
        await Swal.fire({
          title: "Address Added",
          text: "Your address has been saved successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        return created;
      } catch (err) {
        throw err; // Let form handle the error
      }
    },
    [createAddr, handleCloseModal]
  );

  const handleUpdate = useCallback(
    async (id, data) => {
      try {
        const updated = await updateAddr(id, data);
        handleCloseModal();
        
        await Swal.fire({
          title: "Address Updated",
          text: "Your address has been updated successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        return updated;
      } catch (err) {
        throw err; // Let form handle the error
      }
    },
    [handleCloseModal, updateAddr]
  );

  const handleDeleteRequest = useCallback((id) => {
    setDeleteConfirmId(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId) return;
    
    try {
      await deleteAddr(deleteConfirmId);
      setDeleteConfirmId(null);
      
      await Swal.fire({
        title: "Address Deleted",
        text: "The address has been removed.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      setDeleteConfirmId(null);
      await Swal.fire({
        title: "Error",
        text: err?.message || "Failed to delete address. Please try again.",
        icon: "error",
      });
    }
  }, [deleteConfirmId, deleteAddr]);

  const handleSetDefault = useCallback(
    async (id) => {
      try {
        await setDefault(id);
        
        await Swal.fire({
          title: "Default Address Updated",
          text: "This address is now your default.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        await Swal.fire({
          title: "Error",
          text: err?.message || "Failed to set default address. Please try again.",
          icon: "error",
        });
      }
    },
    [setDefault]
  );

  /* ── Loading state ── */
  if (initialLoadPending || (loading && addresses.length === 0)) {
    return <ListSkeleton rows={3} label="Loading saved addresses" />;
  }

  /* ── Error state ── */
  if (error && addresses.length === 0) {
    return (
      <div className="space-y-4">
        <div className={PANEL_CLASS_NAME}>
          <h2 className={`text-sm font-bold tracking-[0.15em] uppercase mb-4 ${PRIMARY_TEXT_CLASS_NAME}`}>
            My Addresses
          </h2>
          <div className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <AlertCircle size={16} className="text-rose-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
              <button
                type="button"
                onClick={handleAddNew}
                className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mt-2 hover:underline"
              >
                Add address manually
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Empty state ── */
  if (!loading && addresses.length === 0) {
    return (
      <div className="space-y-4">
        <div className={PANEL_CLASS_NAME}>
          <h2 className={`text-sm font-bold tracking-[0.15em] uppercase mb-4 ${PRIMARY_TEXT_CLASS_NAME}`}>
            My Addresses
          </h2>
          <div className={`text-center py-12 ${SECONDARY_TEXT_CLASS_NAME}`}>
            <MapPin size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm font-medium mb-6">No saved addresses yet.</p>
            <button
              type="button"
              onClick={handleAddNew}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition active:scale-[0.98]"
            >
              <Plus size={16} />
              Add Your First Address
            </button>
          </div>
        </div>

        <AddressModal
          open={modalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmSelection}
          addresses={addresses}
          selectedId={selectedId}
          loading={false}
          onSelect={setSelectedId}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={deleteAddr}
          onSetDefault={setDefault}
          initialView={modalConfig.initialView}
          initialEditingAddress={modalConfig.initialEditingAddress}
          isDark={isDark}
        />
      </div>
    );
  }

  /* ── Main view with addresses ── */
  return (
    <div className="space-y-4">
      <div className={PANEL_CLASS_NAME}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className={`text-sm font-bold tracking-[0.15em] uppercase ${PRIMARY_TEXT_CLASS_NAME}`}>
            My Addresses
          </h2>
          <button
            type="button"
            onClick={handleAddNew}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition active:scale-[0.98]"
          >
            <Plus size={14} />
            Add New
          </button>
        </div>

        {/* Address Cards */}
        <div className="grid gap-4 sm:grid-cols-2">
          {addresses.map((addr) => {
            const isDefault = isAddressDefault(addr);
            
            return (
              <div
                key={addr.id}
                className={`relative rounded-xl border p-4 transition ${
                  isDefault
                    ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10"
                    : "border-neutral-200 bg-neutral-50/70 hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-950/30 dark:hover:border-neutral-700"
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-semibold ${PRIMARY_TEXT_CLASS_NAME}`}>
                      {addr.full_name}
                    </span>
                    {isDefault && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                        DEFAULT
                      </span>
                    )}
                    {addr.label && (
                      <span className={LABEL_BADGE_CLASS_NAME}>
                        {addr.label}
                      </span>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleEdit(addr)}
                      className={EDIT_BUTTON_CLASS_NAME}
                      aria-label="Edit address"
                      title="Edit"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteRequest(addr.id)}
                      className={DELETE_BUTTON_CLASS_NAME}
                      aria-label="Delete address"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {/* Phone */}
                <p className={`text-xs ${SECONDARY_TEXT_CLASS_NAME} mb-2`}>{addr.phone}</p>

                {/* Address */}
                <p className={`text-xs ${SECONDARY_TEXT_CLASS_NAME} leading-relaxed mb-3`}>
                  {formatAddress(addr)}
                </p>

                {/* Set as Default button */}
                {!isDefault && (
                  <button
                    type="button"
                    onClick={() => handleSetDefault(addr.id)}
                    className={LINK_BUTTON_CLASS_NAME}
                  >
                    <Star size={11} />
                    Set as Default
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Address Modal */}
      <AddressModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSelection}
        addresses={addresses}
        selectedId={selectedId}
        loading={loading}
        onSelect={setSelectedId}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={deleteAddr}
        onSetDefault={setDefault}
        initialView={modalConfig.initialView}
        initialEditingAddress={modalConfig.initialEditingAddress}
        isDark={isDark}
      />

      {/* Delete confirmation dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900">
            <h3 className={`text-sm font-bold mb-2 ${PRIMARY_TEXT_CLASS_NAME}`}>
              Delete Address
            </h3>
            <p className={`text-sm mb-5 ${SECONDARY_TEXT_CLASS_NAME}`}>
              Are you sure you want to remove this address? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className={MODAL_CANCEL_BUTTON_CLASS_NAME}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition active:scale-[0.98]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
